import { App } from "@capacitor/app";
import { useEffect, useState } from "react";
import {
  type AppUpdateAvailability,
  canInstallFromFile,
  checkForAppUpdate,
  downloadLatestUpdate,
} from "../lib/appUpdate";

export function UpdatePrompt() {
  const [availability, setAvailability] = useState<AppUpdateAvailability | null>(null);
  const [dismissedVersion, setDismissedVersion] = useState<string | null>(null);
  const [installing, setInstalling] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!canInstallFromFile()) return;
    let active = true;
    let removeListener: (() => Promise<void>) | null = null;

    const refresh = (force = false) => {
      void checkForAppUpdate({ force })
        .then((result) => {
          if (active) setAvailability(result?.available ? result : null);
        })
        .catch(() => undefined);
    };

    refresh();
    void App.addListener("appStateChange", ({ isActive }) => {
      if (isActive) refresh(true);
    }).then((handle) => {
      if (!active) {
        void handle.remove();
        return;
      }
      removeListener = () => handle.remove();
    });

    return () => {
      active = false;
      if (removeListener) void removeListener();
    };
  }, []);

  if (!availability || dismissedVersion === availability.latestVersion) return null;
  const latestVersion = availability.latestVersion;

  async function installUpdate() {
    setInstalling(true);
    setError(null);
    try {
      await downloadLatestUpdate();
      setDismissedVersion(latestVersion);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "The update could not be opened");
    } finally {
      setInstalling(false);
    }
  }

  return (
    <aside
      className="update-prompt"
      role="dialog"
      aria-modal="false"
      aria-labelledby="update-prompt-title"
    >
      <div>
        <p className="eyebrow">Update available</p>
        <h2 id="update-prompt-title">Just Kasz CRM {availability.latestVersion}</h2>
        <p>
          You have {availability.installedVersion}. Update directly to the latest version—interim
          versions are not required.
        </p>
        {error ? <p className="error-message">{error}</p> : null}
      </div>
      <div className="update-prompt__actions">
        <button
          type="button"
          className="button button--secondary"
          disabled={installing}
          onClick={() => setDismissedVersion(availability.latestVersion)}
        >
          Later
        </button>
        <button
          type="button"
          className="button button--primary"
          disabled={installing}
          onClick={() => void installUpdate()}
        >
          {installing ? "Preparing..." : "Update now"}
        </button>
      </div>
    </aside>
  );
}
